const API_BASE = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000';

class ApiService {
  // Create headers with auth token if available
  getHeaders(token = null, includeAuth = true) {
    const headers = {
      'Content-Type': 'application/json',
    };

    if (includeAuth && token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    return headers;
  }

  // Helper method for authenticated requests
  async makeRequest(url, options = {}, token = null) {
    const headers = { ...options.headers };

    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (response.status === 401) {
      // Token expired or invalid - dispatch event for global auth handler
      console.warn('Authentication failed - dispatching auth expiration event');
      window.dispatchEvent(new CustomEvent('auth:expired', {
        detail: { message: 'Your session has expired. Please log in again.' }
      }));
    }

    return response;
  }
  async previewGoldenFunctions(pyFile, jsonFile, token = null) {
    const formData = new FormData();
    formData.append('file', pyFile);
    formData.append('metadata_file', jsonFile);

    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}/api/models/upload/preview`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Preview failed');
    }

    return response.json();
  }

  async uploadModel(file, modelName, modelArchitecture = 'resnet50', hardwareTarget = null, token = null, metadataFile = null, modelGraphFile = null, readOnlyFunctions = null, constMetaFile = null) {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('model_name', modelName);
    formData.append('model_architecture', modelArchitecture); // Model architecture from registry
    
    // Only append hardware target if specified
    if (hardwareTarget) {
      formData.append('hardware_target', hardwareTarget);
    }

    if (metadataFile) {
      formData.append('metadata_file', metadataFile);
    }

    if (modelGraphFile) {
      formData.append('model_graph_file', modelGraphFile);
    }

    if (constMetaFile) {
      formData.append('const_meta_file', constMetaFile);
    }

    if (readOnlyFunctions && readOnlyFunctions.length > 0) {
      formData.append('read_only_functions', JSON.stringify(readOnlyFunctions));
    }

    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await fetch(`${API_BASE}/api/models/upload`, {
      method: 'POST',
      headers,
      body: formData,
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Upload failed');
    }

    return response.json();
  }

  // NEW MODEL-CENTRIC ENDPOINTS
  async getModelsWithVariants(token = null) {
    const response = await this.makeRequest(`${API_BASE}/api/models/v2/`, {
      method: 'GET',
      headers: this.getHeaders(token)
    }, token);
    if (!response.ok) {
      throw new Error('Failed to fetch models with variants');
    }
    return response.json();
  }

  async getModelWithVariants(modelId, token = null) {
    const response = await this.makeRequest(`${API_BASE}/api/models/v2/${modelId}`, {
      method: 'GET',
      headers: this.getHeaders(token)
    }, token);
    if (!response.ok) {
      throw new Error('Failed to fetch model with variants');
    }
    return response.json();
  }

  async createHardwareVariant(modelId, hardwareTarget, optimizationData = {}, token = null) {
    const requestData = {
      hardware_target: hardwareTarget,
      ...optimizationData
    };

    const response = await this.makeRequest(`${API_BASE}/api/models/v2/${modelId}/hardware-variants`, {
      method: 'POST',
      headers: this.getHeaders(token),
      body: JSON.stringify(requestData)
    }, token);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create hardware variant');
    }

    return response.json();
  }

  async updateHardwareVariant(variantId, updateData, token = null) {
    const response = await this.makeRequest(`${API_BASE}/api/models/v2/hardware-variants/${variantId}`, {
      method: 'PUT',
      headers: this.getHeaders(token),
      body: JSON.stringify(updateData)
    }, token);

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to update hardware variant');
    }

    return response.json();
  }

  // LEGACY ENDPOINT - Backward compatibility
  async getModels(token = null) {
    const response = await this.makeRequest(`${API_BASE}/api/models/`, {
      method: 'GET',
      headers: this.getHeaders(token)
    }, token);

    if (!response.ok) {
      throw new Error('Failed to fetch models');
    }

    return response.json();
  }

  async getModelDetails(modelId, token = null) {
    const response = await this.makeRequest(`${API_BASE}/api/models/${modelId}`, {
      method: 'GET',
      headers: this.getHeaders(token)
    }, token);

    if (!response.ok) {
      throw new Error('Failed to fetch model details');
    }

    return response.json();
  }

  async getModelForHardware(modelId, hardwareType, token = null) {
    const response = await this.makeRequest(`${API_BASE}/api/models/${modelId}/hardware/${hardwareType}`, {
      method: 'GET',
      headers: this.getHeaders(token)
    }, token);

    if (!response.ok) {
      if (response.status === 404) {
        throw new Error('Model not found (404)');
      }
      throw new Error(`Failed to fetch model for hardware (${response.status})`);
    }

    return response.json();
  }

  async getModelsByHardware(hardwareType, token = null) {
    const response = await this.makeRequest(`${API_BASE}/api/models/hardware/${hardwareType}`, {
      method: 'GET',
      headers: this.getHeaders(token)
    }, token);

    if (!response.ok) {
      throw new Error('Failed to fetch models by hardware');
    }

    return response.json();
  }

  async deleteModel(modelId, token = null) {
    const response = await this.makeRequest(`${API_BASE}/api/models/${modelId}`, {
      method: 'DELETE',
      headers: this.getHeaders(token)
    }, token);

    if (!response.ok) {
      throw new Error('Failed to delete model');
    }

    return response.json();
  }

  // README Methods
  async getModelReadme(modelId, token = null) {
    const response = await this.makeRequest(`${API_BASE}/api/models/${modelId}/readme`, {
      method: 'GET',
      headers: this.getHeaders(token)
    }, token);

    if (!response.ok) {
      throw new Error('Failed to get README');
    }

    return response.json();
  }

  async updateModelReadme(modelId, content, token = null) {
    const response = await this.makeRequest(`${API_BASE}/api/models/${modelId}/readme`, {
      method: 'PUT',
      headers: this.getHeaders(token),
      body: JSON.stringify({ content })
    }, token);

    if (!response.ok) {
      throw new Error('Failed to update README');
    }

    return response.json();
  }

  async uploadReadmeFile(modelId, file, token = null) {
    const formData = new FormData();
    formData.append('file', file);

    const headers = {};
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    const response = await this.makeRequest(`${API_BASE}/api/models/${modelId}/readme/upload`, {
      method: 'POST',
      headers,
      body: formData
    }, token);

    if (!response.ok) {
      throw new Error('Failed to upload README file');
    }

    return response.json();
  }

  getAccessToken() {
    // Try to get token from window.authContext if available
    if (typeof window !== 'undefined' && window.authContext?.getAccessToken) {
      return window.authContext.getAccessToken();
    }
    // Fallback - return null, will be handled by the component
    return null;
  }

  async checkHealth() {
    try {
      const response = await fetch(`${API_BASE}/api/ready`);
      return response.ok;
    } catch {
      return false;
    }
  }

  // Generic HTTP methods for AK47 and other APIs
  async get(endpoint, token = null) {
    const response = await this.makeRequest(`${API_BASE}${endpoint}`, {
      method: 'GET',
      headers: this.getHeaders(token)
    }, token);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'GET request failed');
    }

    return response.json();
  }

  async post(endpoint, data = {}, token = null) {
    const response = await this.makeRequest(`${API_BASE}${endpoint}`, {
      method: 'POST',
      headers: this.getHeaders(token),
      body: JSON.stringify(data)
    }, token);

    if (!response.ok) {
      const error = await response.json().catch(() => ({ detail: 'Request failed' }));
      throw new Error(error.detail || 'POST request failed');
    }

    return response.json();
  }
}

const apiService = new ApiService();
export default apiService;